// src/pages/queues/ExpandiScrapingQueue.jsx
import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../utils/axiosInstance";
import ReactPaginate from "react-paginate";
import Select from "react-select";
import { toast } from "react-toastify";
import Fuse from "fuse.js";
import { Loader2, RefreshCw, Search, ExternalLink } from "lucide-react";

const isDateField = (f) => /(_at|_time)$/.test(f);
const toDate = (v) => {
    if (!v) return null;
    const s = typeof v === "string" ? v.replace(/ GMT$/, "") : v;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
};
const fmt = (d) => {
    if (!d) return "-";
    const dt = new Date(String(d).replace(/ GMT$/, ""));
    const day = String(dt.getDate()).padStart(2, "0");
    const mon = dt.toLocaleString("en-AU", { month: "short" });
    const yr = dt.getFullYear();
    const hh = String(dt.getHours()).padStart(2, "0");
    const mm = String(dt.getMinutes()).padStart(2, "0");
    return `${day} ${mon}, ${yr} ${hh}:${mm}`;
};

// --- Shared react-select style (44px controls) ---
const selectStyles44 = {
    control: (p) => ({
        ...p,
        minHeight: 44,
        height: 44,
        borderColor: "#e2e8f0",
        boxShadow: "none",
        borderRadius: 12,
        "&:hover": { borderColor: "#cbd5e1" },
    }),
    valueContainer: (p) => ({ ...p, height: 44 }),
    input: (p) => ({ ...p, margin: 0, padding: 0 }),
    option: (p, s) => ({
        ...p,
        backgroundColor: s.isSelected ? "#0f172a" : s.isFocused ? "#f1f5f9" : undefined,
        color: s.isSelected ? "white" : "#0f172a",
    }),
    menu: (p) => ({ ...p, borderRadius: 12, overflow: "hidden" }),
    menuPortal: (base) => ({ ...base, zIndex: 10000 }),
};

const badgeForStatus = (s) => {
    const v = String(s || "").toLowerCase();
    const base = "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset";
    if (v === "pending") return `${base} bg-amber-50 text-amber-700 ring-amber-600/20`;
    if (v === "uploaded") return `${base} bg-sky-50 text-sky-700 ring-sky-600/20`;
    if (v === "scraped") return `${base} bg-emerald-50 text-emerald-700 ring-emerald-600/20`;
    return `${base} bg-slate-100 text-slate-700 ring-slate-600/20`;
};

export default function ExpandiScrapingQueue() {
    const [rows, setRows] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");

    const DEFAULT_STATUS = { value: "pending", label: "Pending" };
    const statusOptions = useMemo(
        () => [
            { value: "", label: "All statuses" },
            { value: "pending", label: "Pending" },
            { value: "uploaded", label: "Uploaded" },
            { value: "scraped", label: "Scraped" },
        ],
        []
    );
    const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS);

    // Sorting:
    // - relevance: desc by default
    // - created_at: oldest first
    // Default combined ordering: high relevance and old items at top.
    const [sortField, setSortField] = useState("relevance");
    const [sortOrder, setSortOrder] = useState("desc");
    const [createdAtOrder, setCreatedAtOrder] = useState("asc"); // oldest first

    const [currentPage, setCurrentPage] = useState(0);
    const recordsPerPage = 30;

    const [loading, setLoading] = useState(false);

    const fetchQueue = async () => {
        try {
            setLoading(true);
            // Expected API: GET /lead_lists/lead_lists returning list of LeadList dicts
            const resp = await axiosInstance.get("/lead_lists/lead_lists");
            const data = Array.isArray(resp.data) ? resp.data : [];
            setRows(data);
        } catch (e) {
            console.error(e);
            toast.error("Failed to fetch Expandi connect queue.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchQueue();
        setSearchTerm("");
        setCurrentPage(0);
        setStatusFilter(DEFAULT_STATUS);
        setSortField("relevance");
        setSortOrder("desc");
        setCreatedAtOrder("asc");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fuse = useMemo(
        () =>
            new Fuse(rows, {
                keys: ["linkedin", "status"],
                threshold: 0.15,
                ignoreLocation: true,
            }),
        [rows]
    );

    const hasFilters = searchTerm.trim() !== "" || statusFilter.value !== "";

    const processed = useMemo(() => {
        let list = rows || [];

        if (searchTerm.trim()) list = fuse.search(searchTerm.trim()).map((r) => r.item);
        if (statusFilter.value) list = list.filter((r) => (r.status || "").toLowerCase() === statusFilter.value);

        // Combined sort:
        // 1) Primary: relevance (desc by default)
        // 2) Secondary: created_at (oldest first => asc)
        // If user chooses created_at header sort, we still keep relevance as a tie-breaker.
        const getRelevance = (x) => {
            const n = Number(x?.relevance);
            return Number.isFinite(n) ? n : -Infinity;
        };
        const getCreated = (x) => toDate(x?.created_at)?.getTime() ?? Infinity;

        list = [...list].sort((a, b) => {
            const ra = getRelevance(a);
            const rb = getRelevance(b);

            // relevance comparator
            if (sortField === "relevance") {
                if (ra !== rb) return sortOrder === "asc" ? ra - rb : rb - ra;
                const ca = getCreated(a);
                const cb = getCreated(b);
                if (ca !== cb) return createdAtOrder === "asc" ? ca - cb : cb - ca;
                return 0;
            }

            // created_at comparator
            if (sortField === "created_at") {
                const ca = getCreated(a);
                const cb = getCreated(b);
                if (ca !== cb) return createdAtOrder === "asc" ? ca - cb : cb - ca;

                // tie-break by relevance (desc default)
                if (ra !== rb) return rb - ra;
                return 0;
            }

            // fallback: keep the combined rule
            if (ra !== rb) return rb - ra;
            const ca = getCreated(a);
            const cb = getCreated(b);
            if (ca !== cb) return ca - cb;
            return 0;
        });

        return list;
    }, [rows, searchTerm, statusFilter, fuse, sortField, sortOrder, createdAtOrder]);

    const pageCount = Math.ceil(processed.length / recordsPerPage) || 0;
    const displayed = processed.slice(currentPage * recordsPerPage, (currentPage + 1) * recordsPerPage);

    const handleSort = (field) => {
        setCurrentPage(0);

        if (field === "relevance") {
            if (sortField !== "relevance") {
                setSortField("relevance");
                setSortOrder("desc"); // default
            } else if (sortOrder === "desc") {
                setSortOrder("asc");
            } else {
                // back to default combined
                setSortOrder("desc");
            }
            return;
        }

        if (field === "created_at") {
            if (sortField !== "created_at") {
                setSortField("created_at");
                setCreatedAtOrder("asc"); // oldest first default
            } else if (createdAtOrder === "asc") {
                setCreatedAtOrder("desc");
            } else {
                // back to default
                setCreatedAtOrder("asc");
            }
        }
    };

    const columns = [
        { label: "LinkedIn", field: "linkedin" },
        { label: "Status", field: "status" },
        { label: "Relevance", field: "relevance", sortable: true },
        { label: "Created", field: "created_at", sortable: true },
    ];

    const sortIndicator = (field) => {
        if (field === "relevance" && sortField === "relevance") return sortOrder === "asc" ? " ↑" : " ↓";
        if (field === "created_at" && sortField === "created_at") return createdAtOrder === "asc" ? " ↑" : " ↓";
        return "";
    };

    const normalizeLinkedInUrl = (v) => {
        const s = String(v || "").trim();
        if (!s) return "";
        return s.startsWith("http") ? s : `https://${s}`;
    };

    const cardClass = "rounded-2xl border border-slate-200 bg-white shadow-sm";
    const subtleBtn =
        "inline-flex h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-900/20 disabled:opacity-50";
    const pagerBtn = "px-3 py-1 border border-slate-200 rounded-xl bg-white text-slate-700 hover:bg-slate-50";

    return (
        <>
            {/* Controls */}
            <div className={`${cardClass} p-4`}>
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex flex-1 flex-col gap-3 lg:flex-row lg:items-center">
                        <div className="relative flex-1">
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search LinkedIn or status..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(0);
                                }}
                                className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-900/20"
                            />
                        </div>

                        <div className="w-full sm:w-60">
                            <Select
                                value={statusFilter}
                                onChange={(v) => {
                                    setStatusFilter(v);
                                    setCurrentPage(0);
                                }}
                                options={statusOptions}
                                classNamePrefix="react-select"
                                placeholder="Status"
                                styles={selectStyles44}
                            />
                        </div>
                    </div>

                    <button onClick={fetchQueue} disabled={loading} className={subtleBtn} aria-busy={loading}>
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        Refresh
                    </button>
                </div>

                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm text-slate-600">
                        {hasFilters ? `Showing ${processed.length} of ${rows.length} total` : `Total records: ${rows.length}`}
                    </div>
                    <div className="text-xs text-slate-500">
                        Sort:{" "}
                        <span className="font-semibold text-slate-700">
                            {sortField === "relevance"
                                ? `relevance (${sortOrder})`
                                : `created_at (${createdAtOrder})`}
                        </span>
                    </div>
                </div>
            </div>

            {/* Desktop table */}
            <div className={`mt-4 hidden md:block ${cardClass} overflow-hidden`}>
                <div className="overflow-x-auto">
                    <table className="w-full table-auto border-collapse">
                        <thead className="bg-slate-50">
                            <tr className="border-b border-slate-200">
                                {columns.map((col) => (
                                    <th
                                        key={col.field}
                                        onClick={col.sortable ? () => handleSort(col.field) : undefined}
                                        className={[
                                            "px-4 py-3 text-left text-xs font-extrabold uppercase tracking-wide text-slate-700 select-none",
                                            col.sortable ? "cursor-pointer hover:bg-slate-100" : "",
                                        ].join(" ")}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span>{col.label}</span>
                                            <span className="text-slate-400">{col.sortable ? sortIndicator(col.field) : ""}</span>
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {displayed.length ? (
                                displayed.map((rec) => (
                                    <tr key={rec.lead_list_id} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-4 py-3 text-sm text-slate-800 max-w-[420px]">
                                            {rec.linkedin ? (
                                                <a
                                                    href={normalizeLinkedInUrl(rec.linkedin)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="group inline-flex max-w-full items-center gap-2 truncate font-semibold text-slate-900 hover:underline"
                                                >
                                                    <span className="truncate">{rec.linkedin}</span>
                                                    <ExternalLink className="h-4 w-4 text-slate-400 group-hover:text-slate-600 shrink-0" />
                                                </a>
                                            ) : (
                                                <span className="text-slate-500">-</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                            <span className={badgeForStatus(rec.status)}>{rec.status || "n/a"}</span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-slate-800">{rec.relevance ?? "-"}</td>
                                        <td className="px-4 py-3 text-sm text-slate-700">{fmt(rec.created_at)}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td className="px-4 py-10 text-center text-sm text-slate-500" colSpan={columns.length}>
                                        {loading ? (
                                            <span className="inline-flex items-center gap-2">
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                Loading...
                                            </span>
                                        ) : (
                                            "No data"
                                        )}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Mobile cards */}
            <div className="mt-4 block md:hidden space-y-3">
                {displayed.length ? (
                    displayed.map((rec) => (
                        <div key={rec.lead_list_id} className={`${cardClass} p-4`}>
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="text-xs font-semibold text-slate-500">LinkedIn</div>
                                    <div className="mt-1">
                                        {rec.linkedin ? (
                                            <a
                                                href={normalizeLinkedInUrl(rec.linkedin)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 text-sm font-extrabold text-slate-900 hover:underline break-all"
                                            >
                                                {rec.linkedin}
                                                <ExternalLink className="h-4 w-4 text-slate-400 shrink-0" />
                                            </a>
                                        ) : (
                                            <span className="text-sm text-slate-500">-</span>
                                        )}
                                    </div>
                                </div>
                                <span className={badgeForStatus(rec.status)}>{rec.status || "n/a"}</span>
                            </div>

                            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                                <div>
                                    <div className="text-xs font-semibold text-slate-500">Relevance</div>
                                    <div className="text-slate-800">{rec.relevance ?? "-"}</div>
                                </div>
                                <div>
                                    <div className="text-xs font-semibold text-slate-500">Created</div>
                                    <div className="text-slate-800">{fmt(rec.created_at)}</div>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className={`${cardClass} p-8 text-center text-sm text-slate-500`}>
                        {loading ? (
                            <span className="inline-flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Loading...
                            </span>
                        ) : (
                            "No data"
                        )}
                    </div>
                )}
            </div>

            {/* Pagination */}
            {pageCount > 1 && (
                <ReactPaginate
                    previousLabel={<span className="px-3 py-1">← Prev</span>}
                    nextLabel={<span className="px-3 py-1">Next →</span>}
                    pageCount={pageCount}
                    onPageChange={({ selected }) => setCurrentPage(selected)}
                    containerClassName="flex justify-center items-center mt-6 gap-2"
                    pageClassName={pagerBtn}
                    activeClassName="!bg-slate-900 !text-white !border-slate-900 shadow-sm"
                    disabledClassName="opacity-50 cursor-not-allowed"
                    breakClassName="px-3 py-1 text-slate-500"
                    renderOnZeroPageCount={null}
                />
            )}
        </>
    );
}
